import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { SafetyDashboardPayload, VehicleSafetyDetail } from '../../safety-dashboard.model';
import { SafetyDashboardStore } from '../../safety-dashboard.store';
import Chart from 'chart.js/auto';

interface QualityKpi {
  title: string;
  value: string;
  desc: string;
  cls: string;
  icon: string;
}

/**
 * کیفیت داده — KPI و چارت‌ها مستقیم از Table7 (سرور).
 * جدول فقط زیرمجموعه مشکوک‌ها (سقف ۲۰۰ ردیف) تا DOM فریز نشود.
 */
@Component({
  selector: 'app-safety-quality',
  templateUrl: './safety-quality.component.html',
  styleUrls: ['./safety-quality.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SafetyQualityComponent implements OnInit, OnDestroy {
  loading = false;
  hasData = false;
  kpis: QualityKpi[] = [];
  qualityRows: VehicleSafetyDetail[] = [];
  totalSuspicious = 0;

  private sub = new Subscription();
  private charts: any[] = [];
  private payload: SafetyDashboardPayload | null = null;

  private readonly okQuality = 'کیفیت داده مناسب';
  private readonly ROW_CAP = 200;

  constructor(private store: SafetyDashboardStore, private cdr: ChangeDetectorRef) {
    Chart.defaults.font.family = "'vazir', Tahoma, sans-serif";
  }

  ngOnInit(): void {
    this.sub.add(this.store.loading$.subscribe((v) => { this.loading = v; this.cdr.markForCheck(); }));
    this.sub.add(this.store.payload$.subscribe((p) => { this.payload = p; this.refresh(); }));
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.destroyCharts();
  }

  toEn(n: number | null | undefined, fraction = 0): string {
    if (n === null || n === undefined || isNaN(Number(n))) return '—';
    return Number(n).toLocaleString('en-US', { maximumFractionDigits: fraction });
  }

  confClass(v: number | null | undefined): string {
    const c = Number(v) || 0;
    if (c < 70) return 'q-low';
    if (c < 90) return 'q-mid';
    return 'q-ok';
  }

  trackByRow(_: number, x: VehicleSafetyDetail): string {
    return String(x.strUnitID);
  }

  private refresh(): void {
    const p = this.payload;
    this.hasData = !!p && (!!p.dataQuality || p.vehicles.length > 0);
    if (!this.hasData || !p) {
      this.kpis = [];
      this.qualityRows = [];
      this.totalSuspicious = 0;
      this.destroyCharts();
      this.cdr.markForCheck();
      return;
    }
    this.buildKpis(p);
    this.buildTable(p);
    this.cdr.markForCheck();
    const snapshot = p;
    setTimeout(() => this.renderCharts(snapshot), 60);
  }

  private buildKpis(p: SafetyDashboardPayload): void {
    const q = p.dataQuality;
    if (!q) {
      this.kpis = [];
      return;
    }
    this.kpis = [
      { title: 'کیفیت مناسب', value: this.toEn(q.GoodQualityVehicles), desc: 'خودرو', cls: 'p-green', icon: 'fa-check-circle' },
      { title: 'کیفیت پایین', value: this.toEn(q.LowQualityVehicles), desc: 'نیازمند بررسی GPS', cls: 'p-red', icon: 'fa-exclamation-triangle' },
      { title: 'نمونه محدود (مسافت)', value: this.toEn(q.DistanceConfidence0To49), desc: 'اطمینان مسافت ۰-۴۹', cls: 'p-yellow', icon: 'fa-database' },
      { title: 'اطمینان سرعت ۹۰-۱۰۰', value: this.toEn(q.PeakConfidence90To100), desc: 'خودرو', cls: 'p-blue', icon: 'fa-tachometer' },
      { title: 'اطمینان مسافت ۸۰-۱۰۰', value: this.toEn(q.DistanceConfidence80To100), desc: 'خودرو', cls: 'p-blue', icon: 'fa-road' },
      { title: 'کل خودروها', value: this.toEn(q.TotalVehicles), desc: 'نمونه فعلی', cls: 'p-green', icon: 'fa-car' },
    ];
  }

  private buildTable(p: SafetyDashboardPayload): void {
    const suspicious: VehicleSafetyDetail[] = [];
    for (const x of p.vehicles) {
      if (x.DataQuality !== this.okQuality || (Number(x.PeakSpeedConfidence) || 0) < 90) {
        suspicious.push(x);
      }
    }
    this.totalSuspicious = suspicious.length;
    suspicious.sort((a, b) => (Number(a.PeakSpeedConfidence) || 0) - (Number(b.PeakSpeedConfidence) || 0));
    this.qualityRows = suspicious.slice(0, this.ROW_CAP);
  }

  qualityBadge(q: string): string {
    if (q === this.okQuality) return 'b-green';
    return 'b-yellow';
  }

  qualityBadgeLabel(q: string): string {
    if (q === this.okQuality) return 'مشکوک';
    return q || 'بررسی';
  }

  private destroyCharts(): void {
    this.charts.forEach((c) => { try { c.destroy(); } catch {} });
    this.charts = [];
  }

  private canvas(id: string): HTMLCanvasElement | null {
    return document.getElementById(id) as HTMLCanvasElement | null;
  }

  private renderCharts(p: SafetyDashboardPayload): void {
    this.destroyCharts();
    if (!this.hasData || !p.dataQuality) return;
    const q = p.dataQuality;

    const peakEl = this.canvas('qualityPeakConf');
    if (peakEl) {
      this.charts.push(new Chart(peakEl, {
        type: 'bar',
        data: {
          labels: ['۰-۳۰', '۳۱-۶۰', '۶۱-۸۹', '۹۰-۱۰۰'],
          datasets: [{
            label: 'تعداد خودرو',
            data: [q.PeakConfidence0To30, q.PeakConfidence31To60, q.PeakConfidence61To89, q.PeakConfidence90To100],
            backgroundColor: ['#ff5d68', '#ff8b4c', '#f5c451', '#35d07f'],
            borderRadius: 8,
          }],
        },
        options: {
          maintainAspectRatio: false,
          scales: {
            y: { beginAtZero: true, ticks: { color: '#8195a6', font: { size: 10 } }, grid: { color: '#1d2b36' } },
            x: { ticks: { color: '#b9c7d2', font: { size: 11 } }, grid: { display: false } },
          },
          plugins: { legend: { display: false } },
        } as any,
      }));
    }

    const distEl = this.canvas('qualityDistConf');
    if (distEl) {
      this.charts.push(new Chart(distEl, {
        type: 'bar',
        data: {
          labels: ['۰-۴۹', '۵۰-۷۹', '۸۰-۱۰۰'],
          datasets: [{
            label: 'تعداد خودرو',
            data: [q.DistanceConfidence0To49, q.DistanceConfidence50To79, q.DistanceConfidence80To100],
            backgroundColor: ['#ff5d68', '#f5c451', '#35d07f'],
            borderRadius: 8,
          }],
        },
        options: {
          maintainAspectRatio: false,
          scales: {
            y: { beginAtZero: true, ticks: { color: '#8195a6', font: { size: 10 } }, grid: { color: '#1d2b36' } },
            x: { ticks: { color: '#b9c7d2', font: { size: 11 } }, grid: { display: false } },
          },
          plugins: { legend: { display: false } },
        } as any,
      }));
    }
  }
}
